"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const qrCodeLotteryInputs = [
  {
    name: "generalTitle",
    type: "string",
    helperText: "Header title",
    required: true,
    defaultValue: "Welcome to the QR Code Lottery!",
  },
  {
    name: "notLogged",
    type: "object",
    friendlyName: "Not logged in state configuration",
    required: true,
    subFields: [
      { name: "title", type: "string", required: true },
      { name: "description", type: "string", required: true },
      {
        name: "ctaLabel",
        friendlyName: "CTA button label",
        type: "string",
        required: true,
      },
      {
        name: "ctaUrl",
        friendlyName: "CTA button url",
        type: "url",
        required: true,
      },
      {
        name: "secondaryCtaLabel",
        friendlyName: "Secondary CTA button label",
        type: "string",
        required: true,
      },
      {
        name: "secondaryCtaUrl",
        friendlyName: "Secondary CTA button url",
        type: "url",
        required: true,
      },
    ],
    defaultValue: {
      title: "Log In to Play",
      description: "Please log in to participate in the lottery.",
      ctaLabel: "Log In",
      ctaUrl: "#",
      secondaryCtaLabel: "Sign Up",
      secondaryCtaUrl: "##",
    },
  },
  {
    name: "won",
    type: "object",
    friendlyName: "Winning state configuration",
    required: true,
    subFields: [
      { name: "title", type: "string", required: true },
      { name: "description", type: "string", required: true },
      {
        name: "ctaLabel",
        friendlyName: "CTA button label",
        type: "string",
        required: true,
      },
      {
        name: "ctaUrl",
        friendlyName: "CTA button url",
        type: "url",
        required: true,
      },
      {
        name: "secondaryCtaLabel",
        friendlyName: "Secondary CTA button label",
        type: "string",
        required: true,
      },
      {
        name: "secondaryCtaUrl",
        friendlyName: "Secondary CTA button url",
        type: "url",
        required: true,
      },
    ],
    defaultValue: {
      title: "Congratulations  you’ve won {#points#} points",
      description:
        "We’re thrilled to announce that you’re a lucky winner in our draw. We hope you enjoy your prize.",
      ctaLabel: "View points history",
      ctaUrl: "#",
      secondaryCtaLabel: "Redeem rewards",
      secondaryCtaUrl: "##",
    },
  },
  {
    name: "lost",
    type: "object",
    friendlyName: "Losing state configuration",
    required: true,
    subFields: [
      { name: "title", type: "string", required: true },
      { name: "description", type: "string", required: true },
    ],
    defaultValue: {
      title: "Sorry, You Didn't Win",
      description:
        "Unfortunately, you didn't win a prize on this spin. Better luck next time! Keep an eye for more surprises coming soon.",
    },
  },
  {
    name: "alreadyParticipated",
    type: "object",
    friendlyName: "Already participated state configuration",
    required: true,
    subFields: [
      { name: "title", type: "string", required: true },
      { name: "description", type: "string", required: true },
    ],
    defaultValue: {
      title: "Sorry, you have already participated!",
      description:
        "We appreciate your enthusiasm, but you have already participated in this draw. Keep an eye for more surprises coming soon.",
    },
  },
  {
    name: "spin",
    type: "object",
    friendlyName: "Spin state configuration",
    required: true,
    subFields: [
      { name: "title", type: "string", required: true },
      { name: "description", type: "string", required: true },
      { name: "ctaLabel", type: "string", required: true },
    ],
    defaultValue: {
      title: "Spin the Wheel",
      description: "Give it another spin and see if you win!",
      ctaLabel: "Spin now",
    },
  },
  {
    name: "ctasUnhappyPath",
    type: "object",
    friendlyName: "Unhappy path (lost/already participated) CTAs configuration",
    required: true,
    subFields: [
      {
        name: "ctaLabel",
        friendlyName: "CTA button label",
        type: "string",
        required: true,
      },
      {
        name: "ctaUrl",
        friendlyName: "CTA button url",
        type: "url",
        required: true,
      },
      {
        name: "secondaryCtaLabel",
        friendlyName: "Secondary CTA button label",
        type: "string",
        required: true,
      },
      {
        name: "secondaryCtaUrl",
        friendlyName: "Secondary CTA button url",
        type: "url",
        required: true,
      },
    ],
    defaultValue: {
      ctaLabel: "Discover rewards",
      ctaUrl: "#",
      secondaryCtaLabel: "Earn points",
      secondaryCtaUrl: "##",
    },
  },
  {
    name: "backgroundImage",
    type: "file",
    friendlyName: "Background image",
    allowedFileTypes: IMAGE_FILE_TYPES,
    required: true,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff705e82e3ca347cfba04e0cbd64a00df",
  },
  {
    name: "mobileBackgroundImage",
    type: "file",
    friendlyName: "Mobile background image",
    allowedFileTypes: IMAGE_FILE_TYPES,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Ff0ab92dbade941e5ab39f3f5cb216cec",
  },
  {
    name: "wheelPointsLabel",
    type: "richText",
    friendlyName: "Text on the wheel when user wins",
    helperText: "Uses {#points#} placeholder to display the points",
    required: true,
    defaultValue: "{#points#} points",
  },
  {
    name: "wheelLoseLabel",
    type: "richText",
    friendlyName: "Text on the wheel when the user loses",
    required: true,
    defaultValue: "<b>No</b> luck",
  },
  {
    name: "wheelTitle",
    type: "richText",
    friendlyName: "Title on the wheel before it spins",
    required: true,
    defaultValue: "Spin <b>& Win</b>",
  },
] as const satisfies Inputs;

export const QrCodeLotteryComponent: RegisteredComponent = {
  name: "QrCodeLottery",
  component: dynamic(async () =>
    withValidation(
      qrCodeLotteryInputs,
      (await import("./QrCodeLottery")).QrCodeLottery,
      "QrCodeLottery"
    )
  ),
  inputs: qrCodeLotteryInputs,
};
